/**
 * Which duplicate rows the invoice screen shows, and what the row they duplicate says instead
 * (MED-1023).
 *
 * Shared by both tabs on purpose. Payment Activity reads `paymentRequests` and the Ledger reads
 * `invoiceTransactions`, and the two had already drifted apart over how to label a duplicate -
 * the Ledger read the flags, Payment Activity guessed from whether a link existed, so the same
 * row could read `Duplicate` on one tab and `Duplicate - needs review` on the other. One module
 * is what stops that happening again.
 *
 * Kept out of the components and DOM-free, like ledger.js, so the decision that removes 40,349
 * rows from view across the estate can be tested without a DOM.
 */

/**
 * The two duplicate states are different and are labelled differently.
 *
 * A confirmed duplicate is a decision - somebody or something has established that this row
 * re-reports a payment already recorded. The review tier is the absence of a decision, waiting on
 * a person. Both are held out of the balance, but only one of them is an answer.
 *
 * Read from the explicit flags, never inferred from `duplicateOfId`. A missing link says nothing
 * about whether the decision was taken: `isDuplicate` with no link is a confirmed duplicate whose
 * original could not be pointed at, not an unreviewed one. `PaymentRequest` has no awaiting-review
 * field at all - only `isDuplicate` and `duplicateOf` - so on Payment Activity the review badge
 * simply never appears, which is honest about what that entity knows.
 *
 * `noun` only names the thing in the title, since the two tabs count different things: Payment
 * Activity numbers payments, the Ledger numbers transactions.
 */
export const duplicateBadge = (row, { noun = 'transaction' } = {}) => {
  if (row?.isDuplicate) {
    return {
      badge: 'Duplicate',
      badgeTitle: row.duplicateOfId
        ? `Already recorded as ${noun} #${row.duplicateOfId}. Kept for the record; it does not affect the balance.`
        : `A re-report of a ${noun} already recorded, but the original could not be linked, so there is no row to point at. Kept for the record; it does not affect the balance.`,
    };
  }

  if (row?.isAwaitingDuplicateReview) {
    return {
      badge: 'Duplicate - needs review',
      badgeTitle: 'Matched a payment already recorded, but not closely enough to link automatically. Kept out of the balance until somebody decides.',
    };
  }

  return { badge: null, badgeTitle: null };
};

/**
 * A duplicate may only be hidden if there is a row left on screen that says it existed.
 *
 * That is the whole safety condition of this feature, and since there is no global "show all"
 * checkbox it is the ONLY thing keeping a hidden row reachable. The marker on the original is the
 * one control that can bring the row back, so a duplicate with nothing to hang a marker on must
 * not be hidden - it would simply vanish, with nothing anywhere on the screen able to return it.
 *
 * Hence all three conditions below, and hence: **do not loosen this without adding a way to
 * reveal a row that has no original.** That is the mistake this comment exists to stop. Widening
 * it by one clause - hiding a row with a null `duplicateOfId`, or one whose original sits on
 * another invoice - makes those rows permanently unreachable rather than merely hidden.
 *
 * 0 rows in `payment_request` have a null `duplicate_of_id` today, but the flagging runs again on
 * live data, so this is handled rather than assumed.
 *
 * Awaiting review is deliberately not hidable either. It is work somebody still has to do, and
 * until MED-976 gives it a review screen this tab is the only place it is visible at all.
 */
const isHidable = (row, originalIds) => Boolean(row?.isDuplicate) && row?.duplicateOfId != null && originalIds.has(row.duplicateOfId);

const markerText = (count, revealed) => {
  if (count === 0) {
    return null;
  }

  return `${count} ${count === 1 ? 'duplicate' : 'duplicates'} ${revealed ? 'shown' : 'hidden'}`;
};

/**
 * Decide which rows to render, and tell each original how many duplicates hang off it.
 *
 * Call this at render time, on rows that are already built. In particular do NOT filter the input
 * to `buildLedgerRows`: that walks every row to carry the running balance, giving a non-counting
 * row the previous balance so a duplicate is visibly inert. Drop rows before it and the balance
 * column silently changes - the arithmetic must be identical whether or not the rows are on
 * screen.
 *
 * `revealedOriginalIds` holds the originals whose marker has been clicked. That is the only way
 * to reveal anything: reveal is per group, so opening one original's duplicates does not show
 * every duplicate on the invoice. It is component state and resets with the page by design - see
 * the ticket: a sticky "show" somebody forgets is a smaller problem than a sticky "hide" they
 * did not.
 */
export const buildDuplicateVisibility = (rows, { revealedOriginalIds = [] } = {}) => {
  const all = Array.isArray(rows) ? rows : [];
  const originalIds = new Set(all.map((row) => row?.id));
  const revealed = new Set(revealedOriginalIds);

  // How many hidable duplicates point at each original, so the original can say so.
  const counts = new Map();
  all.forEach((row) => {
    if (isHidable(row, originalIds)) {
      counts.set(row.duplicateOfId, (counts.get(row.duplicateOfId) ?? 0) + 1);
    }
  });

  let hiddenCount = 0;
  let duplicateCount = 0;

  const visible = [];

  all.forEach((row) => {
    if (isHidable(row, originalIds)) {
      duplicateCount += 1;

      if (!revealed.has(row.duplicateOfId)) {
        hiddenCount += 1;
        return;
      }
    }

    const count = counts.get(row?.id) ?? 0;
    const duplicatesRevealed = count > 0 && revealed.has(row.id);

    // Rows keep their original position, so a revealed duplicate reappears between the rows it
    // sat between rather than at the end - which is what makes the balance column readable.
    visible.push({
      ...row,
      duplicateCount: count,
      duplicatesRevealed,
      duplicateMarker: markerText(count, duplicatesRevealed),
    });
  });

  return { rows: visible, duplicateCount, hiddenCount };
};
